import { Link } from "wouter";
import { ArrowLeft, FileText } from "lucide-react";
import { Footer } from "@/components/website/Footer";

const SECTIONS = [
  { id: "agreement", label: "Agreement to Terms" },
  { id: "service", label: "The Service" },
  { id: "accounts", label: "Accounts & Access" },
  { id: "subscription", label: "Subscription & Billing" },
  { id: "acceptable-use", label: "Acceptable Use" },
  { id: "data-ownership", label: "Data Ownership" },
  { id: "ip", label: "Intellectual Property" },
  { id: "disclaimers", label: "Disclaimers" },
  { id: "limitation", label: "Limitation of Liability" },
  { id: "indemnification", label: "Indemnification" },
  { id: "termination", label: "Termination" },
  { id: "governing-law", label: "Governing Law" },
  { id: "changes", label: "Changes to Terms" },
  { id: "contact", label: "Contact" },
];

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-white/95 border-b border-slate-200 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link href="/" className="flex items-center gap-2.5">
            <img src="/images/finproject-logo.png" alt="FinProject" className="h-8 w-auto" />
            <span className="font-bold text-slate-900 hidden sm:inline">FinProject</span>
          </Link>
          <Link href="/" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-16 flex gap-12">
        <aside className="hidden lg:block w-56 shrink-0">
          <div className="sticky top-28">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Contents</p>
            <nav className="space-y-1">
              {SECTIONS.map(s => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="block text-sm text-slate-500 hover:text-slate-900 py-1 transition-colors"
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        <article className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 100%)" }}>
              <FileText className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">Terms of Service</h1>
          </div>
          <p className="text-sm text-slate-500 mb-10">
            Last updated: <strong>27 March 2026</strong> · Effective date: 27 March 2026
          </p>

          <div className="prose prose-slate max-w-none space-y-12">

            <section id="agreement">
              <h2 className="text-xl font-bold text-slate-900 mb-4">1. Agreement to Terms</h2>
              <p className="text-slate-600 leading-relaxed">
                These Terms of Service ("Terms") constitute a legally binding agreement between you (individually or on behalf of your organisation, "Customer", "you") and <strong>Business Elite LLC</strong>, a company registered in the Shams Free Zone, United Arab Emirates ("FinProject", "Company", "we", "us", or "our"), governing your access to and use of the FinProject platform at <strong>finproject.io</strong> (the "Service").
              </p>
              <p className="text-slate-600 leading-relaxed mt-3">
                By creating an account, clicking "I agree", or otherwise accessing or using the Service, you confirm that you have read, understood, and agree to be bound by these Terms and our <Link href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>. If you do not agree, do not use the Service.
              </p>
              <p className="text-slate-600 leading-relaxed mt-3">
                If you are using the Service on behalf of an organisation, you represent that you have authority to bind that organisation to these Terms.
              </p>
            </section>

            <hr className="border-slate-200" />

            <section id="service">
              <h2 className="text-xl font-bold text-slate-900 mb-4">2. The Service</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                FinProject is a cloud-based Software-as-a-Service (SaaS) platform designed for construction companies to manage project finances, including client invoicing, subcontractor and supplier management, payroll, budgeting, BOQ, cash flow, and related financial workflows.
              </p>
              <p className="text-slate-600 leading-relaxed mb-3">
                We reserve the right to modify, suspend, or discontinue any feature of the Service at any time with reasonable notice. We will endeavour to provide at least 14 days' advance notice for material changes.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <p className="text-amber-800 text-sm leading-relaxed">
                  <strong>No Financial or Legal Advice:</strong> FinProject is a data management tool. Nothing in the Service constitutes accounting, tax, legal, or financial advice. You are responsible for ensuring your use of the platform complies with applicable laws and regulations in your jurisdiction, including VAT, ZATCA, or other local requirements.
                </p>
              </div>
            </section>

            <hr className="border-slate-200" />

            <section id="accounts">
              <h2 className="text-xl font-bold text-slate-900 mb-4">3. Accounts & Access</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p><strong className="text-slate-800">Registration:</strong> You must provide accurate, complete, and current information when creating an account. You agree to update your information to keep it accurate.</p>
                <p><strong className="text-slate-800">Age:</strong> You must be at least 18 years old to use the Service. By creating an account, you confirm you meet this requirement.</p>
                <p><strong className="text-slate-800">Account Security:</strong> You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account. Notify us immediately at <a href="mailto:support@finproject.io" className="text-blue-600 hover:underline">support@finproject.io</a> if you suspect unauthorised access.</p>
                <p><strong className="text-slate-800">Demo Accounts:</strong> Free demo workspaces are provided for evaluation purposes only. Demo data is automatically purged after the trial period. We reserve the right to terminate demo accounts at any time.</p>
                <p><strong className="text-slate-800">Team Members:</strong> As an account administrator, you may invite team members and are responsible for their compliance with these Terms.</p>
              </div>
            </section>

            <hr className="border-slate-200" />

            <section id="subscription">
              <h2 className="text-xl font-bold text-slate-900 mb-4">4. Subscription & Billing</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">4.1 Plans</h3>
                  <p className="text-slate-600 leading-relaxed">
                    FinProject offers paid subscription plans ("Plans") with varying features and usage limits. Current plan details and pricing are available on our <Link href="/#pricing" className="text-blue-600 hover:underline">Pricing page</Link>. We may introduce, modify, or retire Plans with reasonable notice.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">4.2 Payment Processing (Stripe)</h3>
                  <p className="text-slate-600 leading-relaxed">
                    All payments for FinProject subscriptions are processed by <strong>Stripe, Inc.</strong>, a PCI DSS Level 1 certified third-party payment processor. FinProject does not store, process, or have access to your full payment card details. By subscribing, you agree to be bound by Stripe's <a href="https://stripe.com/legal/ssa" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Services Agreement</a> and <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a>. Stripe may collect cardholder data, billing address, and device information directly for payment processing and fraud prevention.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">4.3 Billing Cycles</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Subscriptions are billed in advance on a monthly or annual cycle, depending on your selected plan. Annual plans are billed as a single payment equivalent to 10 months of the monthly rate (2 months free). Prices are quoted in US Dollars (USD) and exclusive of any applicable taxes.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">4.4 Taxes</h3>
                  <p className="text-slate-600 leading-relaxed">
                    You are responsible for all applicable taxes, duties, levies, or similar governmental assessments associated with your purchase, excluding taxes on FinProject's net income. Where required by law, FinProject may collect and remit VAT or similar taxes.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">4.5 Automatic Renewal</h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                    <p className="text-slate-700 leading-relaxed text-sm">
                      <strong>Your subscription will renew automatically</strong> at the end of each billing period (monthly or annually) unless you cancel before the renewal date. By subscribing, you authorise FinProject (via Stripe) to charge your payment method on file for each renewal at the then-current rate. Where practicable, we will send an email reminder before each renewal. You may cancel automatic renewal at any time via your account settings or by contacting <a href="mailto:support@finproject.io" className="text-blue-600 hover:underline">support@finproject.io</a>.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">4.6 Free Trial</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Certain Plans may include a free trial period. At the end of the trial, your subscription will automatically convert to a paid plan and your payment method will be charged unless you cancel before the trial ends.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">4.7 Price Changes</h3>
                  <p className="text-slate-600 leading-relaxed">
                    We may change subscription prices with at least 30 days' prior notice by email. Your continued use of the Service after the price change takes effect constitutes acceptance of the new price.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">4.8 Failed Payments</h3>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-2">
                    <p className="text-slate-700 leading-relaxed text-sm">
                      If a scheduled payment fails (e.g., expired card, insufficient funds), the following process applies:
                    </p>
                    <ul className="list-disc list-inside text-slate-700 space-y-1 text-sm">
                      <li><strong>Immediate notification:</strong> You will receive an email asking you to update your payment method.</li>
                      <li><strong>Retry schedule:</strong> Stripe will automatically retry the charge up to 3 times over the following 7 days.</li>
                      <li><strong>Grace period:</strong> Your access continues uninterrupted during the 7-day retry window.</li>
                      <li><strong>Suspension:</strong> If payment is not resolved within 7 days, your account will be suspended. You will retain read-only access to export your data, but will not be able to create or edit records until payment is restored.</li>
                      <li><strong>Re-activation:</strong> Update your payment method in your account settings or contact support. Once payment is successfully processed, full access is restored immediately.</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">4.9 Disputes & Chargebacks</h3>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-2">
                    <p className="text-slate-600 leading-relaxed text-sm">
                      If you believe a charge is incorrect or unauthorised, please contact us at <a href="mailto:support@finproject.io" className="text-blue-600 hover:underline">support@finproject.io</a> <strong>before</strong> initiating a dispute or chargeback with your bank or card issuer. We are committed to resolving billing issues quickly and fairly.
                    </p>
                    <p className="text-slate-600 leading-relaxed text-sm">
                      Filing a chargeback without first contacting FinProject may result in temporary suspension of your account while the dispute is investigated. Chargebacks incur processing fees from Stripe, and fraudulent or abusive chargebacks may result in permanent account termination.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                  <h3 className="font-semibold text-slate-800 mb-2">4.10 Refunds</h3>
                  <p className="text-slate-600 leading-relaxed text-sm">
                    Please refer to our <Link href="/refund-policy" className="text-blue-600 hover:underline">Refund Policy</Link> for full details on cancellations and refunds.
                  </p>
                </div>
              </div>
            </section>

            <hr className="border-slate-200" />

            <section id="acceptable-use">
              <h2 className="text-xl font-bold text-slate-900 mb-4">5. Acceptable Use</h2>
              <p className="text-slate-600 leading-relaxed mb-4">You agree not to:</p>
              <ul className="space-y-2 text-slate-600 text-sm">
                {[
                  "Use the Service for any unlawful purpose or in violation of any applicable laws or regulations",
                  "Attempt to gain unauthorised access to any part of the Service, its servers, or any other system connected to the Service",
                  "Upload or transmit viruses, malware, or any other malicious code",
                  "Scrape, crawl, or systematically extract data from the Service without our prior written consent",
                  "Resell, sublicense, or create derivative works based on the Service without express permission",
                  "Impersonate any person or entity, or misrepresent your affiliation with any person or entity",
                  "Use the Service to process or store data that infringes any third party's intellectual property rights",
                  "Conduct load testing or performance testing without prior written approval",
                  "Circumvent any usage limits or technical restrictions imposed by your Plan",
                ].map((item, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="w-5 h-5 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0 text-xs font-bold mt-0.5">✕</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-slate-600 leading-relaxed mt-5">
                Violation of this section may result in immediate suspension or termination of your account without notice or refund.
              </p>
            </section>

            <hr className="border-slate-200" />

            <section id="data-ownership">
              <h2 className="text-xl font-bold text-slate-900 mb-4">6. Data Ownership</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                <strong className="text-slate-800">Your data is yours.</strong> You retain all ownership of the financial records, project data, documents, and other content you create or upload ("Customer Data").
              </p>
              <p className="text-slate-600 leading-relaxed mb-3">
                By using the Service, you grant FinProject a limited, non-exclusive, royalty-free licence to host, store, process, and transmit your Customer Data solely to provide and maintain the Service.
              </p>
              <p className="text-slate-600 leading-relaxed mb-3">
                You can export your data at any time from within the application. Upon termination, you may request a data export within 30 days, after which your data will be deleted (subject to legal retention obligations).
              </p>
              <p className="text-slate-600 leading-relaxed">
                We may use aggregated, anonymised, and de-identified data (from which no individual or company can be identified) to improve the Service and publish industry benchmarks.
              </p>
            </section>

            <hr className="border-slate-200" />

            <section id="ip">
              <h2 className="text-xl font-bold text-slate-900 mb-4">7. Intellectual Property</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                The Service, including all software, algorithms, interfaces, design, trademarks, logos, and content created by FinProject, is and remains the exclusive property of Business Elite LLC. Nothing in these Terms transfers any IP rights to you.
              </p>
              <p className="text-slate-600 leading-relaxed">
                We grant you a limited, non-exclusive, non-transferable, revocable licence to access and use the Service during your subscription term, solely for your internal business purposes.
              </p>
            </section>

            <hr className="border-slate-200" />

            <section id="disclaimers">
              <h2 className="text-xl font-bold text-slate-900 mb-4">8. Disclaimers</h2>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-3">
                <p className="text-amber-900 text-sm leading-relaxed">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
                </p>
                <p className="text-amber-900 text-sm leading-relaxed">
                  WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE. YOU ASSUME ALL RISK FOR ANY DAMAGE TO YOUR COMPUTER SYSTEM, LOSS OF DATA, OR OTHER HARM THAT RESULTS FROM YOUR USE OF THE SERVICE.
                </p>
                <p className="text-amber-900 text-sm leading-relaxed">
                  WE ARE NOT RESPONSIBLE FOR ANY FINANCIAL DECISIONS MADE BASED ON DATA OR REPORTS GENERATED BY THE SERVICE.
                </p>
              </div>
            </section>

            <hr className="border-slate-200" />

            <section id="limitation">
              <h2 className="text-xl font-bold text-slate-900 mb-4">9. Limitation of Liability</h2>
              <div className="bg-slate-100 border border-slate-200 rounded-xl p-5 space-y-3">
                <p className="text-slate-700 text-sm leading-relaxed">
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, FINPROJECT AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, GOODWILL, OR BUSINESS INTERRUPTION, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICE.
                </p>
                <p className="text-slate-700 text-sm leading-relaxed">
                  OUR TOTAL CUMULATIVE LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATING TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE GREATER OF (a) THE AMOUNT YOU PAID TO US IN THE 12 MONTHS PRECEDING THE CLAIM, OR (b) USD $100.
                </p>
              </div>
            </section>

            <hr className="border-slate-200" />

            <section id="indemnification">
              <h2 className="text-xl font-bold text-slate-900 mb-4">10. Indemnification</h2>
              <p className="text-slate-600 leading-relaxed">
                You agree to indemnify, defend, and hold harmless FinProject and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or related to: (a) your use of the Service; (b) your violation of these Terms; (c) your violation of any third-party rights; or (d) any Customer Data you upload or process through the Service.
              </p>
            </section>

            <hr className="border-slate-200" />

            <section id="termination">
              <h2 className="text-xl font-bold text-slate-900 mb-4">11. Termination</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p><strong className="text-slate-800">By You:</strong> You may cancel your subscription at any time via your account settings. Cancellation takes effect at the end of the current billing period. You will retain access to the Service until then. See our <Link href="/refund-policy" className="text-blue-600 hover:underline">Refund Policy</Link> for details.</p>
                <p><strong className="text-slate-800">By Us:</strong> We may suspend or terminate your account immediately if: you materially breach these Terms; you fail to pay amounts due; or we believe continued access poses a security or legal risk. We will provide notice where legally permitted and practicable.</p>
                <p><strong className="text-slate-800">Effect of Termination:</strong> Upon termination, your right to access the Service ceases immediately. Provisions that by their nature should survive termination (including data ownership, IP, disclaimers, limitation of liability) shall survive.</p>
              </div>
            </section>

            <hr className="border-slate-200" />

            <section id="governing-law">
              <h2 className="text-xl font-bold text-slate-900 mb-4">12. Governing Law & Disputes</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                These Terms are governed by and construed in accordance with the laws of the United Arab Emirates, without regard to conflict of law principles. The courts of Sharjah, UAE shall have exclusive jurisdiction over any disputes arising from these Terms or the Service.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Before initiating any formal dispute, you agree to contact us at <a href="mailto:support@finproject.io" className="text-blue-600 hover:underline">support@finproject.io</a> and allow 30 days for good-faith resolution.
              </p>
            </section>

            <hr className="border-slate-200" />

            <section id="changes">
              <h2 className="text-xl font-bold text-slate-900 mb-4">13. Changes to These Terms</h2>
              <p className="text-slate-600 leading-relaxed">
                We may update these Terms from time to time. We will notify you of material changes by email at least 14 days before the new terms take effect. Your continued use of the Service after the effective date constitutes acceptance of the updated Terms. If you do not agree with the changes, you must cancel your subscription before the effective date.
              </p>
            </section>

            <hr className="border-slate-200" />

            <section id="contact">
              <h2 className="text-xl font-bold text-slate-900 mb-4">14. Contact</h2>
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <p className="text-slate-700 font-semibold mb-4">Business Elite LLC</p>
                <div className="text-slate-600 space-y-1 text-sm">
                  <p>Shams Free Zone, Sharjah, United Arab Emirates</p>
                  <p>Email: <a href="mailto:support@finproject.io" className="text-blue-600 hover:underline">support@finproject.io</a></p>
                </div>
              </div>
            </section>

          </div>
        </article>
      </div>

      <Footer />
    </div>
  );
}
